import {
  IOrder, IProductType,
  CATEGORIES_TYPES, IOrderLine, ORDER_DELIVERY_METHODS, IOrderBakery
} from '@cakeiteasy/web-utils';

import {
  IAddPaymentInfoPayload,
  IAddShippingInfoPayload,
  IAddToCartPayload,
  IUserDataPaymentInfo,
  IAuthTrackPayload, IVirtualPageViewPayload,
} from '../interfaces';
import { POST_HOG_EVENT_NAME } from '../constants';
import { ICart } from '../../cart/models/cart/i-cart';
import { ICartLine } from '../../cart/models/cart/i-cart-line';
import { ICakeCartLine } from '../../cart/models/cart/i-cake-cart-line';
import { getCakeCartLineTotalPrice } from '../../cart/helpers/cart-helper';
import { IPaymentInfo } from '../../payment';
import { CART_LINE_TYPES } from '../../cart/constants/common';
import { IUser } from '../../users/models/i-user';
import {
  capitalizeString,
  floatFormatPrice,
  getCartLineTrackingNameFn,
  getDiscountOrderLine,
  getProductTrackingName,
  getTotalPriceValue
} from './shared-analytic.helpers';

const {
  VIRTUAL_PAGE_VIEW,
  VIEW_ITEM,
  ADD_TO_CART,
  REMOVE_FROM_CART,
  VIEW_CART,
  BEGIN_CHECKOUT,
  ADD_SHIPPING_INFO,
  ADD_PAYMENT_INFO,
  PURCHASE,
  SIGN_UP,
  LOGIN,
  LOGOUT,
  SEARCH,
  SEARCH_NO_RESULTS,
} = POST_HOG_EVENT_NAME;

export interface IPostHogIdentifyPayload {
  distinctId: string;
  properties: Record<string, string | number | boolean>;
}

const removeEmptyProperties = (properties: Record<string, any>): Record<string, string | number | boolean> =>
  Object.keys(properties).reduce((acc, key) => {
    const value = properties[key];
    return value === undefined || value === null || value === ''
      ? acc
      : { ...acc, [key]: value };
  }, {} as Record<string, string | number | boolean>);

const toIdentifyPayload = (
  distinctId: string | number | undefined | null,
  properties: Record<string, any>,
): IPostHogIdentifyPayload | null => {
  if (!distinctId) {
    return null;
  }

  return {
    distinctId: String(distinctId),
    properties: removeEmptyProperties(properties),
  };
};

const collectUserData = (cart: ICart, contactInfo: IPaymentInfo): IUserDataPaymentInfo => {
  const {
    deliveryOrder: { type, deliveryPhone, deliveryAddress, deliveryCity, deliveryPostNumber, deliveryUserName },
    company, bakery,
  } = cart;

  return {
    isB2B: Boolean(company),
    phoneNumber: deliveryPhone,
    email: contactInfo && contactInfo.userEmail,
    address: {
      userName: deliveryUserName,
      country: bakery.country.code,
      ...(type === ORDER_DELIVERY_METHODS.DELIVERY_TO_DOOR && {
        street: deliveryAddress,
        city: deliveryCity,
        postalCode: deliveryPostNumber,
      })
    },
  };
};

const getAuthPayload = (payload: IAuthTrackPayload) => {
  const { method, isB2B } = payload;
  return {
    method: method,
    customer_role: getCustomerRole(isB2B),
  };
};

const mapUserToIdentify = (
  user: IUser,
  properties: Record<string, string | number | boolean> = {},
): IPostHogIdentifyPayload | null =>
  toIdentifyPayload(user?.id, {
    email: user?.email || user?.contactEmail,
    phone: user?.phone,
    name: user?.fullName,
    customer_role: getCustomerRole(Boolean((user as any)?.companyName)),
    ...(user?.lastLogin && { last_login: new Date(user.lastLogin).toISOString() }),
    ...(user?.registrationDate && { signup_date: new Date(user.registrationDate).toISOString() }),
    ...properties,
  });

const mapAuthToIdentify = (
  payload: IAuthTrackPayload,
  properties: Record<string, string | number | boolean> = {},
): IPostHogIdentifyPayload | null =>
  toIdentifyPayload(payload?.userId, {
    email: payload?.email,
    phone: payload?.phone,
    customer_role: getCustomerRole(Boolean(payload?.isB2B)),
    ...properties,
  });

const mapLoginToIdentify = (payload: IAuthTrackPayload): IPostHogIdentifyPayload | null =>
  mapAuthToIdentify(payload, { last_login: new Date().toISOString() });

const mapSignUpToIdentify = (payload: IAuthTrackPayload): IPostHogIdentifyPayload | null =>
  mapAuthToIdentify(payload, { signup_date: new Date().toISOString() });

const getSignUpTrack = (payload: IAuthTrackPayload): [string, any] => {
  return [SIGN_UP, getAuthPayload(payload)];
};

const getLoginTrack = (payload: IAuthTrackPayload): [string, any] => {
  return [LOGIN, getAuthPayload(payload)];
};

const getLogoutTrack = (): [string, any] => {
  return [LOGOUT, null];
};

const getVirtualPageViewTrack = (data: IVirtualPageViewPayload): [string, any] => {
  const { pageTitle, pageFullUrl, previousUrl } = data;

  return [VIRTUAL_PAGE_VIEW,
    {
      $current_url: pageFullUrl,
      $referrer: previousUrl,
      page_title: pageTitle,
    }
  ];
};

const getSearchTrack = (data: { searchTerm: string, pageLocation: string; }): [string, any] => {
  const { searchTerm, pageLocation } = data;
  return [SEARCH, {
    search_term: searchTerm,
    page_location: pageLocation,
  }];
};

const trackSearchNoResult = (data: { searchTerm: string, pageLocation: string; }): [string, any] => {
  const { searchTerm, pageLocation } = data;
  return [SEARCH_NO_RESULTS, {
    search_term: searchTerm,
    page_location: pageLocation,
  }];
};

const mapPaymentToIdentify = (
  userData: IUserDataPaymentInfo,
  properties: Record<string, string | number | boolean> = {},
): IPostHogIdentifyPayload | null =>
  toIdentifyPayload(userData?.userId, {
    email: userData?.email,
    phone: userData?.phoneNumber,
    customer_role: getCustomerRole(userData?.isB2B),
    name: userData?.address?.userName,
    city: userData?.address?.city,
    postal_code: userData?.address?.postalCode,
    street: userData?.address?.street,
    country: userData?.address?.country,
    ...properties,
  });

const getViewItemTrack = (product: IProductType): [string, any] => {
  const { bakery: { country: { currency } } } = product;
  return [VIEW_ITEM,
    {
      product_id: product.id,
      product_name: product.name,
      category: product.category?.name || 'Cake',
      bakery: product.bakery.name,
      currency: currency,
    }];
};

const mapOrderToIdentify = (
  order: IOrder,
  properties: Record<string, string | number | boolean> = {},
): IPostHogIdentifyPayload | null =>
  toIdentifyPayload(order?.multiOrder?.customer?.id, {
    email: order?.multiOrder?.customer?.contactEmail || order?.multiOrder?.customer?.email || order?.multiOrder?.userEmail,
    customer_role: getCustomerRole(Boolean(order?.multiOrder?.company)),
    phone: order?.deliveryOrder?.deliveryPhone || order?.multiOrder?.customer?.phone || order?.multiOrder?.userPhone,
    name: order?.deliveryOrder?.deliveryUserName || order?.multiOrder?.customer?.fullName || order?.multiOrder?.userName,
    city: order?.deliveryOrder?.deliveryCity,
    postal_code: order?.deliveryOrder?.deliveryPostNumber,
    street: order?.deliveryOrder?.deliveryAddress,
    country: order?.bakery?.country?.code,
    ...properties,
  });

const mapOrderPurchaseToIdentify = (order: IOrder): IPostHogIdentifyPayload | null =>
  mapOrderToIdentify(order, {
    last_purchase_date: new Date().toISOString(),
    last_purchase_value: floatFormatPrice(order?.priceWithoutVat || 0),
  });

const mapOrderLinesToTrack = (line: IOrderLine, bakery: IOrderBakery) => {
  const details = line.details;

  const categories = (details?.categories || [])
    .filter((category: { type: string; name: string }) => category.type === CATEGORIES_TYPES.GLOBAL)
    .map((category: { type: string; name: string }) => category.name);

  return {
    bakery: bakery.name,
    price: floatFormatPrice(line.price),
    quantity: line.quantity,
    categoryName: categories,
    ...(details && {
      product_id: details.type.id || null,
      product_name: details ? details.type.id : null,
      variant: details.applyOnce ? line.name : details.variant?.name,
    })
  };
};

const getOrderPurchaseTrack = (order: IOrder): [string, any] => {
  const discountLine = getDiscountOrderLine(order.multiOrder.orderLines);
  const deliveryPrice = order.deliveryOrder?.price || 0;
  const itemCount = order.orderLines.reduce((acc, { quantity }) => (acc += quantity, acc), 0);
  const { multiOrder: { company } } = order;

  return [PURCHASE, {
    transaction_id: order.multiOrder.number,
    value: floatFormatPrice(order.priceWithoutVat),
    revenue: floatFormatPrice(order.priceWithoutVat),
    currency: order.bakery.country.currency,
    tax: floatFormatPrice(order.price - order.priceWithoutVat),
    shipping: floatFormatPrice(deliveryPrice),
    total_value: floatFormatPrice(order.price + deliveryPrice),
    item_count: itemCount,
    items: order.orderLines.map((line) => mapOrderLinesToTrack(line, order.bakery)),
    current_bakery: order.bakery.name,
    customer_role: Boolean(company) ? 'b2b' : 'b2c',
    coupon: discountLine?.details?.code,
  }];
};

const getPaymentTrack = (payload: IAddPaymentInfoPayload): [string, any] => {
  const { lines, paymentType, bakery, discount, giftCard } = payload;
  const { country: { currency }, name: bakeryName } = bakery;

  return [ADD_PAYMENT_INFO, {
    payment_type: capitalizeString(paymentType),
    cart_value: floatFormatPrice(getTotalPriceValue(lines)),
    currency,
    item_count: getItemsCount(lines),
    current_bakery: bakeryName,
    ...(discount && { coupon: discount }),
    ...(giftCard && { gift_card: giftCard }),
    items: lines.map((line) =>
      mapCartLinesToTrack({
        line,
        bakery,
        getNameFn: getProductTrackingName,
      })),
  }];
};

const getItemsCount = (lines: { count: number }[]): number => {
  return lines.reduce((acc, line) => (acc += line.count, acc), 0);
};

const getAddShippingInfoTrack = (payload: IAddShippingInfoPayload): [string, any] => {
  return makeShippingEvent(ADD_SHIPPING_INFO, payload);
};

const getBeginCheckoutTrack = (payload: IAddShippingInfoPayload): [string, any] => {
  return makeShippingEvent(BEGIN_CHECKOUT, payload);
};

const makeShippingEvent = (eventName: string, { lines, shippingMethod, bakery }: IAddShippingInfoPayload): [string, any] => {
  const { country: { currency }, name: bakeryName } = bakery;

  return [eventName, {
    cart_value: floatFormatPrice(getTotalPriceValue(lines)),
    currency: currency,
    item_count: getItemsCount(lines),
    items: lines.map((line) =>
      mapCartLinesToTrack({
        line,
        bakery,
        getNameFn: getProductTrackingName,
      })),
    current_bakery: bakeryName,
    ...(shippingMethod && { shipping_tier: capitalizeString(shippingMethod) }),
  }];
};

const getAddToCartTrack = (payload: IAddToCartPayload): [string, any] => {
  const { bakery, categoryName, subCategoryName, line, lines, upsellLines } = payload;
  const inputParams = { bakery, categoryName, subCategoryName };
  const { country: { currency } } = bakery;
  const items = [];
  let value = 0;

  if (line) {
    value += getTotalPriceValue([line]);
    items.push(mapCartLinesToTrack({ line, ...inputParams, getNameFn: getCartLineTrackingNameFn() }));
  }

  if (lines) {
    value += getTotalPriceValue(lines);
    items.push(...lines.map(
      (l) =>
        mapCartLinesToTrack({ line: l, ...inputParams, getNameFn: getCartLineTrackingNameFn() }))
    );
  }

  if (upsellLines) {
    value += getTotalPriceValue(upsellLines);
    items.push(...upsellLines.map(
      l => mapCartLinesToTrack(
        { line: l, ...inputParams, getNameFn: getCartLineTrackingNameFn(true) }
      ))
    );
  }

  return [ADD_TO_CART, { currency, current_bakery: bakery.name, cart_value: floatFormatPrice(value), items }];
};

const mapCartLinesToTrack = (payload: IAddToCartPayload & { getNameFn: (data: ICakeCartLine) => string }) => {
  const { line, getNameFn, bakery: { name: bakeryName }, categoryName, subCategoryName } = payload;
  const { product } = line;

  return {
    product_id: line.product ? line.product.type.id : null,
    product_name: getNameFn(line),
    bakery: bakeryName,
    variant: product?.variant?.name || null,
    price: floatFormatPrice(getCakeCartLineTotalPrice({ line, isPurePrice: true })),
    quantity: line.count,
    ...(categoryName && { category: categoryName }),
    ...(subCategoryName && { category2: subCategoryName }),
  };
};

const getRemoveFromCartTrack = (cart: ICart, lines: ICartLine[]): [string, any] => {
  const { bakery: { name: bakeryName, country: { currency } } } = cart;

  return [REMOVE_FROM_CART, {
    currency,
    current_bakery: bakeryName,
    items: lines.map(l => ({ ...mapCartLineToViewCartTrack(l), bakery: bakeryName })),
  }];
};

const getViewCartTrack = (cart: ICart): [string, any] => {
  const { price, bakery: { name: bakeryName, country: { currency } } } = cart;

  return [VIEW_CART, {
    currency,
    current_bakery: bakeryName,
    value: floatFormatPrice(price),
    items: cart.lines.map(l => ({ ...mapCartLineToViewCartTrack(l), bakery: bakeryName })),
  }];
};

const mapCartLineToViewCartTrack = (line: ICartLine) => {
  const { product, price } = line;

  return {
    product_id: product ? product.type.id : null,
    product_name: line.name,
    variant: product?.variant?.name || null,
    price: floatFormatPrice(price),
    quantity: line.quantity || line.count,
  };
};

const getCustomerRole = (isB2B: boolean) => isB2B ? 'b2b' : 'b2c';

export const postHogHelper = {
  collectUserData,
  getSignUpTrack,
  getLoginTrack,
  getLogoutTrack,
  getVirtualPageViewTrack,
  getSearchTrack,
  trackSearchNoResult,
  getViewItemTrack,
  getOrderPurchaseTrack,
  getPaymentTrack,
  getAddShippingInfoTrack,
  getBeginCheckoutTrack,
  getAddToCartTrack,
  getRemoveFromCartTrack,
  getViewCartTrack,

  mapUserToIdentify,
  mapAuthToIdentify,
  mapPaymentToIdentify,
  mapOrderToIdentify,
  mapOrderPurchaseToIdentify,
  mapLoginToIdentify,
  mapSignUpToIdentify,
};
